// frontend/src/components/Common/FileUpload.jsx

import React, { useState, useEffect, useRef } from 'react';
import { attachmentsAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { usePermissions } from '../../context/PermissionsContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';
import { FiUpload, FiFile, FiDownload, FiTrash2, FiX, FiPaperclip, FiRefreshCw } from 'react-icons/fi';
import './FileUpload.css';

// AuthImage fetches the exact same URL as handleDownload's window.open —
// guaranteeing the same auth path that already works.
const AuthImage = ({ attachmentId, alt, getDownloadUrl }) => {
  const [blobUrl, setBlobUrl] = React.useState(null);
  const objectUrlRef = React.useRef(null);

  React.useEffect(() => {
    if (!attachmentId || !getDownloadUrl) return;

    const url = getDownloadUrl(attachmentId); // same URL window.open uses
    const token = localStorage.getItem('accessToken');

    fetch(url, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then(res => {
        if (!res.ok) throw new Error(`${res.status}`);
        return res.blob();
      })
      .then(blob => {
        const objUrl = URL.createObjectURL(blob);
        objectUrlRef.current = objUrl;
        setBlobUrl(objUrl);
      })
      .catch(err => console.warn('AuthImage failed for id:', attachmentId, err.message));

    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [attachmentId, getDownloadUrl]);

  if (!blobUrl) return <FiFile size={20} />;
  return <img src={blobUrl} alt={alt} className="file-thumbnail" />;
};

const FileUpload = ({ 
  entityType, 
  entityId, 
  category = '',
  label = 'Attachments',
  maxFiles = 20,
  maxFileSize = 50 * 1024 * 1024, // 50MB
  onUploadComplete,
  compact = false,
  inline = false,
  sideBySide = false,
  pendingMode = false,
  pendingFiles = [],
  onPendingFilesChange,
  excludeCategories = [],
}) => {
  const { t } = useTranslation();
  const { currentUser } = usePermissions();
  const { user: authUser } = useAuth();   // JWT-decoded user (tamper-proof)
  const { toast_error, toast_success } = useToast();
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Load existing attachments if entityId exists (edit mode)
  useEffect(() => {
    if (entityId) {
      loadExistingAttachments();
    }
  }, [entityId, entityType, category]);

  const loadExistingAttachments = async () => {
    try {
      setLoading(true);
      const response = await attachmentsAPI.getAttachments(entityType, entityId, category);
      
      if (response.data.success) {
        const all = response.data.attachments || [];
        const filtered = excludeCategories.length
          ? all.filter(f => !excludeCategories.includes(f.category || ''))
          : all;
        setExistingAttachments(filtered);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error loading attachments:', err);
      setError('Failed to load attachments');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = async (files) => {
    if (files.length === 0) return;

    // Validate file sizes
    const oversizedFiles = files.filter(f => f.size > maxFileSize);
    if (oversizedFiles.length > 0) {
      alert(t('attachments.file_too_large', `Some files exceed the ${Math.round(maxFileSize / 1024 / 1024)}MB limit`));
      return;
    }

    const currentCount = pendingFiles.length + existingAttachments.length;
    if (currentCount + files.length > maxFiles) {
      alert(t('attachments.max_files', `Maximum ${maxFiles} files allowed`));
      return;
    }

    // In pending mode OR when we have entityId with pending files: add to pending
    if (pendingMode || (entityId && onPendingFilesChange)) {
      if (onPendingFilesChange) {
        onPendingFilesChange([...pendingFiles, ...files]);
      }
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // When no entityId (new form), add to pending
    if (!entityId && onPendingFilesChange) {
      onPendingFilesChange([...pendingFiles, ...files]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // NORMAL MODE: Upload immediately (only if no pending mode and no entityId)
    if (!pendingMode && !entityId) {
      alert('Cannot upload: No entity ID provided');
      return;
    }
  };

  const handleDownload = (attachmentId) => {
    const downloadUrl = attachmentsAPI.download(attachmentId);
    window.open(downloadUrl, '_blank');
  };

  // to handle open clicking on filename
  const handleOpenFile = (file) => {
    if (!file?.isExisting || !file?.attachment_id) return;
    handleDownload(file.attachment_id);
  };

  const canDeleteFile = (file) => {
    const uid = authUser?.user_id || currentUser?.user_id;
    if (!uid) return false;
    // is_admin comes from signed JWT — cannot be manipulated by the user
    const isAdmin = authUser?.is_admin === true ||
                    authUser?.group_name === 'ADMIN' ||
                    currentUser?.groups?.includes('ADMIN');
    const isOwner = file.uploaded_by && String(file.uploaded_by) === String(uid);
    return isAdmin || isOwner;
  };

  const handleDeleteExisting = async (file) => {
    if (!canDeleteFile(file)) {
      toast_error(t('attachments.no_permission', 'Não tem permissão para eliminar este ficheiro.'));
      return;
    }
    if (!window.confirm(t('attachments.confirm_delete', 'Eliminar este ficheiro?'))) return;
    try {
      const response = await attachmentsAPI.delete(file.attachment_id);
      if (response.data.success) {
        setExistingAttachments(prev =>
          prev.filter(a => a.attachment_id !== file.attachment_id));
        toast_success(t('attachments.deleted', 'Ficheiro eliminado.'));
      }
    } catch (err) {
      toast_error(err.response?.data?.error || 'Erro ao eliminar ficheiro');
    }
  }
  const handleDeletePending = (index) => {
    if (onPendingFilesChange) {
      const newFiles = pendingFiles.filter((_, i) => i !== index);
      onPendingFilesChange(newFiles);
    }
  };

  const handleSupersede = async (file) => {
    if (!window.confirm(
      t('attachments.confirm_supersede', `Marcar "${file.original_filename}" como substituído? O ficheiro ficará registado mas oculto.`)
    )) return;
    try {
      await attachmentsAPI.supersede(file.attachment_id);
      loadExistingAttachments();
      toast_success(t('attachments.superseded', 'Ficheiro marcado como substituído.'));
    } catch (err) {
      toast_error(err.response?.data?.error || 'Erro ao substituir ficheiro');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const d    = new Date(dateString);
    const dd   = String(d.getDate()).padStart(2, '0');
    const mo   = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh   = String(d.getHours()).padStart(2, '0');
    const min  = String(d.getMinutes()).padStart(2, '0');
    return `${dd}-${mo}-${yyyy} ${hh}:${min}`;
  };

  // ✅ Check if file is an image
  const isImageFile = (file) => {
    if (file.mime_type) return file.mime_type.startsWith('image/');
    if (file.type) return file.type.startsWith('image/');
    return false;
  };

  // AuthImage defined above as top-level component

  // Combine existing and pending files for display
  const allFiles = [
    ...existingAttachments.map(att => ({ ...att, isExisting: true })),
    ...pendingFiles.map((file, index) => ({
      original_filename: file.name,
      file_size: file.size,
      mime_type: file.type,
      fileObject: file, // ✅ Store File object for preview
      isPending: true,
      pendingIndex: index,
      uploaded_at: new Date().toISOString(),
      uploaded_by: null,
      uploaded_by_name: null
    }))
  ];

  // SIDE-BY-SIDE LAYOUT
  if (sideBySide) {
    return (
      <div className="file-upload-side-by-side">
        {/* Left: Upload Box */}
        <div className="upload-section">
          <div
            className={`drop-zone-small ${dragActive ? 'active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <FiUpload size={24} />
            <p>{t('attachments.drop_files', 'Drop files')}</p>
            <span className="or-text">{t('or', 'or')}</span>
            <button type="button" className="btn-browse">
              {t('attachments.browse', 'Browse')}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        {/* Right: File List */}
        <div className="files-section">
          <h4>{t('attachments.imported_files', 'Imported Files')}</h4>
          
          {loading && (
            <div className="loading-files">Loading...</div>
          )}

          {allFiles.length === 0 && !loading && (
            <div className="no-files">
              {t('attachments.no_files', 'No files uploaded')}
            </div>
          )}

          {allFiles.length > 0 && (
            <div className="file-list-scrollable">
              {allFiles.map((file) => (
                <div
                  key={file.attachment_id || `pending-${file.pendingIndex}`}
                  className={`file-item ${file.isPending ? 'pending' : ''} ${file.is_superseded ? 'superseded' : ''}`}
                >
                  {/* File icon / thumbnail */}
                  <div className="file-icon">
                    {isImageFile(file) ? (
                      file.isPending ? (
                        <img
                          src={URL.createObjectURL(file.fileObject)}
                          alt={file.original_filename}
                          className="file-thumbnail"
                        />
                      ) : (
                        <AuthImage attachmentId={file.attachment_id} alt={file.original_filename} getDownloadUrl={attachmentsAPI.download} />
                      )
                    ) : (
                      <FiFile size={20} />
                    )}
                  </div>

                  {/* File info */}
                  <div className="file-info">
                    {file.isExisting ? (
                      <button
                        type="button"
                        className="file-name file-name-button"
                        onClick={() => handleOpenFile(file)}
                        title={t('download', 'Download')}
                      >
                        {file.original_filename}
                      </button>
                    ) : (
                      <div className="file-name">{file.original_filename}</div>
                    )}
                    <div className="file-meta-row">
                      {file.isPending && <span className="pending-tag">{t('pending', 'Pending')}</span>}
                      {!file.isPending && (
                        <>
                          {file.uploaded_by_name && (
                            <span className="file-uploader">{file.uploaded_by_name}</span>
                          )}
                          <span className="file-date">{formatDate(file.uploaded_at)}</span>
                          {file.is_superseded && (
                            <span className="superseded-badge" title={formatDate(file.superseded_at)}>
                              <FiRefreshCw size={10} /> substituído
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="file-actions">
                    {file.isExisting && !file.is_superseded && (
                      <button
                        type="button"
                        onClick={() => handleSupersede(file)}
                        className="btn-file-action btn-supersede"
                        title={t('attachments.supersede', 'Marcar como substituído')}
                      >
                        <FiRefreshCw size={14} />
                      </button>
                    )}
                    {file.isExisting && (
                      <button
                        type="button"
                        onClick={() => handleDownload(file.attachment_id)}
                        className="btn-file-action"
                        title={t('download', 'Download')}
                      >
                        <FiDownload size={16} />
                      </button>
                    )}
                    {!file.is_superseded && (
                      <button
                        type="button"
                        onClick={() => file.isPending ? handleDeletePending(file.pendingIndex) : handleDeleteExisting(file)}
                        className="btn-file-action btn-delete"
                        title={t('delete', 'Delete')}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ... rest of the component (inline, compact, default modes) stays the same
  // INLINE MODE
  if (inline) {
    return (
      <div className="file-upload-inline">
        <button
          type="button"
          className="btn-upload-inline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          title={label}
        >
          <FiPaperclip size={14} />
          <span className="upload-count">{allFiles.length}</span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        {allFiles.length > 0 && (
          <div className="inline-files-tooltip">
            {allFiles.map((item) => (
              <div key={item.id || item.attachment_id} className="inline-file-item">
                <FiFile size={12} />
                <span>{item.original_filename}</span>
                {item.isPending && <span className="pending-mark">*</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // COMPACT MODE
  if (compact) {
    return (
      <div className="file-upload-compact">
        <div
          className={`file-drop-zone-compact ${dragActive ? 'active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <FiUpload size={20} />
          <span>{t('attachments.drag_drop_compact', 'Drop files or click')}</span>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        {uploading && (
          <div className="upload-progress-compact">
            <div className="spinner-sm"></div>
            <span>{t('uploading', 'Uploading...')}</span>
          </div>
        )}
        
        {allFiles.length > 0 && (
          <div className="file-list-compact">
            <table className="compact-files-table">
              <thead>
                <tr>
                  <th>{t('attachments.filename', 'Filename')}</th>
                  <th className="text-center">{t('attachments.size', 'Size')}</th>
                  <th className="text-center">{t('attachments.uploaded', 'Data')}</th>
                  <th style={{ width: '90px' }}>{t('actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {allFiles.map((item) => (
                  <tr
                    key={item.attachment_id || `pending-${item.pendingIndex}`}
                    className={`${item.isPending ? 'pending-file' : ''} ${item.is_superseded ? 'superseded-row' : ''}`}
                  >
<td>
  <div className="compact-file-name">
    {item.is_superseded
      ? <FiRefreshCw size={14} style={{ color: 'var(--gray-400)' }} title="substituído" />
      : <FiFile size={14} />
    }
    {item.isExisting ? (
      <button
        type="button"
        className={`compact-file-name-button${item.is_superseded ? ' superseded-name' : ''}`}
        onClick={() => handleOpenFile(item)}
        title={t('download', 'Download')}
      >
        {item.original_filename}
      </button>
    ) : (
      <span>{item.original_filename}</span>
    )}
    {item.isPending && <span className="pending-indicator">*</span>}
    {item.is_superseded && (
      <span className="superseded-badge-sm" title={formatDate(item.superseded_at)}>substituído</span>
    )}
  </div>
</td>
                    <td className="text-center text-muted">{formatFileSize(item.file_size)}</td>
                    <td className="text-center text-muted" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
                      {item.isPending ? '—' : formatDate(item.uploaded_at)}
                    </td>
                    <td>
                      <div className="compact-file-actions">
                        {item.isExisting && !item.is_superseded && (
                          <button
                            type="button"
                            onClick={() => handleSupersede(item)}
                            className="btn-icon-compact btn-supersede"
                            title={t('attachments.supersede', 'Marcar como substituído')}
                          >
                            <FiRefreshCw size={13} />
                          </button>
                        )}
                        {item.isExisting && (
                          <button
                            type="button"
                            onClick={() => handleDownload(item.attachment_id)}
                            className="btn-icon-compact"
                            title={t('download', 'Download')}
                          >
                            <FiDownload size={14} />
                          </button>
                        )}
                        {!item.is_superseded && (
                          <button
                            type="button"
                            onClick={() => item.isPending ? handleDeletePending(item.pendingIndex) : handleDeleteExisting(item)}
                            className="btn-icon-compact btn-delete"
                            title={t('delete', 'Delete')}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // DEFAULT FULL MODE
  return (
    <div className="file-upload">
      <div className="file-upload-header">
        <h4>{label}</h4>
        <span className="file-count">
          {allFiles.length} / {maxFiles} {t('attachments.files', 'files')}
          {pendingFiles.length > 0 && (
            <span className="pending-badge">{pendingFiles.length} {t('attachments.pending', 'pending')}</span>
          )}
        </span>
      </div>

      <div
        className={`file-drop-zone ${dragActive ? 'active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <FiUpload size={32} />
        <p>{t('attachments.drag_drop', 'Drag & drop files here or click to browse')}</p>
        <span className="file-size-limit">
          {t('attachments.max_size', 'Max size')}: {Math.round(maxFileSize / 1024 / 1024)}MB
        </span>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>

      {uploading && (
        <div className="upload-progress">
          <div className="spinner"></div>
          <span>{t('uploading', 'Uploading...')}</span>
        </div>
      )}

      {error && (
        <div className="upload-error">{error}</div>
      )}

      {allFiles.length > 0 && (
        <div className="file-list">
          <table>
            <thead>
              <tr>
                <th>{t('attachments.filename', 'Filename')}</th>
                <th className="text-center">{t('attachments.size', 'Size')}</th>
                <th className="text-center">{t('attachments.uploaded', 'Uploaded')}</th>
                <th className="text-center">{t('attachments.uploaded_by', 'By')}</th>
                <th style={{ width: '100px' }}>{t('actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {allFiles.map((item) => (
                <tr
                  key={item.attachment_id || `pending-${item.pendingIndex}`}
                  className={`${item.isPending ? 'pending-row' : ''} ${item.is_superseded ? 'superseded-row' : ''}`}
                >
<td>
  <div className="file-name-cell">
    {item.is_superseded
      ? <FiRefreshCw size={16} style={{ color: 'var(--gray-400)' }} title="substituído" />
      : <FiFile size={16} />
    }
    {item.isExisting ? (
      <button
        type="button"
        className={`file-name-link-button${item.is_superseded ? ' superseded-name' : ''}`}
        onClick={() => handleOpenFile(item)}
        title={t('download', 'Download')}
      >
        {item.original_filename}
      </button>
    ) : (
      <span>{item.original_filename}</span>
    )}
    {item.isPending && <span className="pending-indicator">*</span>}
    {item.is_superseded && (
      <span className="superseded-badge" title={`Substituído em: ${formatDate(item.superseded_at)}`}>
        substituído
      </span>
    )}
  </div>
</td>
                  <td className="text-center">{formatFileSize(item.file_size)}</td>
                  <td className="text-center text-muted" style={{ whiteSpace: 'nowrap' }}>
                    {item.isPending ? t('attachments.pending', 'Pending') : formatDate(item.uploaded_at)}
                  </td>
                  <td className="text-center text-muted">
                    {item.isPending ? '—' : (item.uploaded_by_name || '—')}
                  </td>
                  <td>
                    <div className="action-buttons">
                      {item.isExisting && !item.is_superseded && (
                        <button
                          type="button"
                          onClick={() => handleSupersede(item)}
                          className="btn-action btn-supersede"
                          title={t('attachments.supersede', 'Marcar como substituído')}
                        >
                          <FiRefreshCw size={15} />
                        </button>
                      )}
                      {item.isExisting && (
                        <button
                          type="button"
                          onClick={() => handleDownload(item.attachment_id)}
                          className="btn-action btn-view"
                          title={t('download', 'Download')}
                        >
                          <FiDownload size={16} />
                        </button>
                      )}
                      {!item.is_superseded && (
                        <button
                          type="button"
                          onClick={() => item.isPending ? handleDeletePending(item.pendingIndex) : handleDeleteExisting(item)}
                          className="btn-action btn-delete"
                          title={t('delete', 'Delete')}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loading && allFiles.length === 0 && (
        <div className="loading-attachments">
          <div className="spinner"></div>
          <span>{t('loading', 'Loading...')}</span>
        </div>
      )}
    </div>
  );
};

export default FileUpload;